import React from 'react';
import { Helmet } from 'react-helmet';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';

const PageBackground = styled(Box)(({ theme }) => ({
  backgroundColor: '#212121', // Dark grey background
  minHeight: '100vh',
  width: '100%',
  padding: '20px',
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
  },
}));

const Container = styled(Box)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  marginTop: '40px',
  padding: '30px',
  backgroundColor: '#1a237e',
  borderRadius: '10px',
  boxShadow: '0px 0px 20px 0px #00BFFF',
  color: '#FFFFFF',
  fontFamily: "'Roboto', sans-serif",
  [theme.breakpoints.down('sm')]: {
    marginTop: '20px',
    padding: '15px',
    borderRadius: '8px',
  },
}));

const Header = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  fontSize: '2rem',
  textAlign: 'center',
  marginBottom: '20px',
  textShadow: '0 0 10px #FFFFFF, 0 0 20px #00BFFF, 0 0 30px #00BFFF',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
    marginBottom: '15px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  fontSize: '1.2rem',
  marginTop: '20px',
  marginBottom: '10px',
  textDecoration: 'underline',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    marginTop: '15px',
    marginBottom: '8px',
  },
}));

const Paragraph = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  marginBottom: '10px',
  lineHeight: 1.5,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    marginBottom: '8px',
    lineHeight: 1.4,
  },
}));

const BitWheelInfo = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <PageBackground>
      <Helmet>
        <title>Satoshi Advance | Bit Wheel</title>
        <meta
          name="description"
          content="Learn about Bit Wheel - a strategic spinning game where you bet on colors and numbers to maximize your winnings."
        />
        <meta name="robots" content="index,follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0" />
        <link rel="canonical" href="https://bitwheel.satoshiadvance.com/bit-wheel" />
      </Helmet>

      <Container>
        <Header>Bit Wheel</Header>
        <Paragraph sx={{ fontStyle: 'italic' }}>Last updated: January 2025</Paragraph>

        <SectionTitle>Where Strategy Meets the Spin</SectionTitle>
        <Paragraph>
          Welcome to Bit Wheel, a captivating game of chance and strategy where every spin holds the promise of victory. Unlike traditional games of pure luck, Bit Wheel challenges you to make calculated decisions with limited resources, creating an engaging experience that rewards both intuition and strategic thinking.
        </Paragraph>

        <SectionTitle>The Fundamentals: How Bit Wheel Works</SectionTitle>
        <Paragraph>
          At its heart, Bit Wheel is elegantly simple yet surprisingly deep. Each game session begins with you receiving 10 free spins and 10 spin points—your currency for placing bets. The wheel features 42 colorful segments, each representing either Bitcoin (orange) or Satoshi (blue), with corresponding numbers that determine your potential winnings.
        </Paragraph>
        <Paragraph>
          The twist? You must strategically allocate your 10 spin points across up to 10 spins. You can bet 0, 1, or 2 points on each spin, but there's a catch: you must either bet on every spin or skip strategically by betting 0 points. This creates an engaging decision-making process where resource management becomes as important as predicting the wheel's outcome.
        </Paragraph>

        <SectionTitle>The Art of Betting: Colors and Numbers</SectionTitle>
        <Paragraph>
          Bit Wheel offers two distinct betting strategies, each with its own risk-reward profile:
        </Paragraph>

        <SectionTitle>Color Betting: Bitcoin vs. Satoshi</SectionTitle>
        <Paragraph>
          The simplest approach is betting on colors. Will the wheel land on Bitcoin (orange) or Satoshi (blue)? This straightforward choice makes color betting perfect for beginners or those who prefer a more relaxed gameplay style. Watch as the wheel spins and reveals whether your color prediction was correct.
        </Paragraph>

        <SectionTitle>Number Betting: Higher Risk, Higher Reward</SectionTitle>
        <Paragraph>
          For the strategic minded, number betting offers a more challenging path. Each of the 42 segments corresponds to a specific number, and each number carries its own winning amount. The segment number toggle feature reveals the complete layout, showing you exactly which numbers occupy which positions on the wheel. Study the distribution, identify patterns, and place your bets accordingly.
        </Paragraph>

        <SectionTitle>The Five-Spin Revelation</SectionTitle>
        <Paragraph>
          One of Bit Wheel's most distinctive features is its unique spin mechanism. When you place a bet, the wheel doesn't just spin once—it spins five consecutive times in rapid succession, revealing five separate results. This multi-spin approach adds layers of excitement and strategy, as you watch multiple outcomes unfold from a single bet placement.
        </Paragraph>

        <SectionTitle>Strategic Depth: Managing Your Points</SectionTitle>
        <Paragraph>
          The 10-point system creates fascinating strategic decisions. Do you bet conservatively with 1 point per spin, spreading your risk across all 10 available spins? Or do you go aggressive, betting 2 points on your most confident predictions and potentially exhausting your points in just 5 spins? Perhaps you'll adopt a mixed strategy, saving higher bets for moments when you feel most confident.
        </Paragraph>
        <Paragraph>
          The requirement to bet on every spin (or deliberately skip with 0 points) prevents purely passive play and keeps you engaged throughout the entire game session. Every decision matters, and the cumulative effect of your choices determines your final success.
        </Paragraph>

        <SectionTitle>The Segment Number Toggle: Knowledge is Power</SectionTitle>
        <Paragraph>
          To help you make informed decisions, Bit Wheel includes a segment number overlay feature. With a simple toggle, you can view a detailed representation of the game board showing which numbers occupy which segments. Larger numbers appear in circles around the gameboard, giving you a clear visual reference for your betting strategy.
        </Paragraph>
        <Paragraph>
          This transparency sets Bit Wheel apart from pure chance games. You're not betting blind—you have access to complete information about the wheel's layout, allowing you to develop informed strategies based on number distribution and your betting preferences.
        </Paragraph>

        <SectionTitle>Game Flow: From Start to Finish</SectionTitle>
        <Paragraph>
          A typical Bit Wheel session follows this rhythm:
        </Paragraph>
        <Paragraph>
          <strong>1. Receive Your Resources:</strong> Start with 10 free spins and 10 spin points.
        </Paragraph>
        <Paragraph>
          <strong>2. Study the Wheel:</strong> Use the segment toggle to understand the layout and identify your target colors or numbers.
        </Paragraph>
        <Paragraph>
          <strong>3. Place Your Bet:</strong> Choose color or number, then decide how many points to wager (0, 1, or 2).
        </Paragraph>
        <Paragraph>
          <strong>4. Watch the Five-Spin Sequence:</strong> The wheel spins five times consecutively, revealing your results.
        </Paragraph>
        <Paragraph>
          <strong>5. Repeat and Strategize:</strong> Continue betting until you've used all 10 spins or exhausted your 10 points.
        </Paragraph>
        <Paragraph>
          <strong>6. Collect Your Winnings:</strong> Successful predictions earn you points based on the winning amounts associated with your chosen colors or numbers.
        </Paragraph>

        <SectionTitle>The Psychology of the Wheel</SectionTitle>
        <Paragraph>
          What makes Bit Wheel truly engaging isn't just the mechanics—it's the psychological journey. The anticipation as the wheel begins its five-spin sequence, the strategic calculation before each bet placement, the satisfaction of a correct prediction, and the learning opportunity from each outcome combine to create a deeply immersive experience.
        </Paragraph>
        <Paragraph>
          The limited resource system (10 points maximum) creates natural tension and forces meaningful decisions. Unlike games where you can bet indefinitely, Bit Wheel's constraints make every choice significant. This scarcity principle enhances engagement and makes victories feel more earned and satisfying.
        </Paragraph>

        <SectionTitle>Fair Play and Transparency</SectionTitle>
        <Paragraph>
          Bit Wheel is built on principles of fairness and transparency. The wheel's outcomes are determined by verifiable random processes, and the segment number toggle ensures you always know exactly what you're betting on. There are no hidden mechanics or secret algorithms—just pure strategy, informed decision-making, and the excitement of the spin.
        </Paragraph>

        <SectionTitle>Beyond the Basics: Advanced Strategies</SectionTitle>
        <Paragraph>
          As you gain experience with Bit Wheel, you'll discover advanced strategies. Some players prefer color betting for consistent, lower-risk returns. Others master the number distribution and place high-stakes bets on specific segments. The five-spin revelation mechanism rewards those who understand probability and can identify favorable betting opportunities.
        </Paragraph>
        <Paragraph>
          The key to Bit Wheel mastery isn't just understanding the rules—it's developing your own approach that balances risk tolerance, strategic thinking, and resource management. Will you be conservative and steady, or bold and aggressive? The choice is yours, and that freedom of approach is what makes Bit Wheel endlessly replayable.
        </Paragraph>

        <SectionTitle>The Future of Strategic Spinning</SectionTitle>
        <Paragraph>
          Bit Wheel represents a new evolution in spinning games—one that respects player intelligence and rewards strategic thinking. It's not about mindlessly pulling a lever and hoping for the best. It's about making informed decisions, managing resources wisely, and experiencing the thrill of successful predictions backed by your own analysis.
        </Paragraph>
        <Paragraph>
          Whether you're a casual player enjoying the colorful spins or a strategic thinker optimizing every point allocation, Bit Wheel offers an experience tailored to your preferred playstyle. The combination of accessibility and strategic depth creates a game that's easy to learn but challenging to master.
        </Paragraph>

        <Paragraph sx={{ marginTop: isMobile ? '15px' : '20px', fontWeight: 'bold' }}>
          So spin the wheel, make your choices, and experience the perfect blend of chance and strategy. In Bit Wheel, every spin tells a story, every bet carries weight, and every game is a fresh opportunity to test your skills against the wheel. Welcome to the future of strategic gaming—welcome to Bit Wheel.
        </Paragraph>
      </Container>
    </PageBackground>
  );
};

export default BitWheelInfo;
